// src/components/lesson/diagrams/DecisionTree.tsx
//
// A yes/no flowchart for "is this an X?" patterns. Stacks branches
// vertically with indentation so deep trees don't squeeze leaf text
// into unreadable narrow columns. Each node gets full width regardless
// of depth.
//
// Params:
//   {
//     "root": {
//       "question": "Are both sides complete sentences?",
//       "yes": { "question": "...", "yes": { "answer": "Use a semicolon" }, ...},
//       "no":  { "answer": "Use a comma" }
//     }
//   }
//
// Each node is either a decision (`{ question, yes, no }`) or a
// terminal answer (`{ answer }`). Visual connectors:
//   - Cyan question box, full width
//   - Indented YES branch (emerald connector)
//   - Indented NO branch (amber connector)
//   - Emerald answer box at terminals
//
// Earlier version used a side-by-side grid which halved width at each
// level — fine for 1-2 levels, broken for 3+ (text wrapped to 2-3 chars
// per line). Vertical layout scales to any depth.

type DecisionNode = { question: string; yes: DecisionNode; no: DecisionNode } | { answer: string }

type DecisionTreeParams = {
  root?: DecisionNode
  title?: string
  label?: string
}

function isAnswer(n: DecisionNode): n is { answer: string } {
  return typeof (n as { answer?: unknown }).answer === 'string'
}

function Node({ node, isRoot = false }: { node: DecisionNode; isRoot?: boolean }) {
  if (isAnswer(node)) {
    return (
      <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-900 dark:text-emerald-100">
        → {node.answer}
      </div>
    )
  }
  return (
    <div className="space-y-2">
      <div className="rounded-md border border-brand/40 bg-brand/10 px-3 py-2 text-sm text-brand-ink">
        {isRoot ? '' : ''}
        {node.question}
      </div>

      {/* YES branch */}
      <div className="ml-4 border-l-2 border-emerald-500/30 pl-4">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-400">
          Yes ↓
        </div>
        <Node node={node.yes} />
      </div>

      {/* NO branch */}
      <div className="ml-4 border-l-2 border-amber-500/30 pl-4">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-400">
          No ↓
        </div>
        <Node node={node.no} />
      </div>
    </div>
  )
}

export default function DecisionTree({ params }: { params: Record<string, unknown> }) {
  const p = params as DecisionTreeParams
  if (!p.root) return null
  return (
    <figure
      // #44: no role="img" — a decision tree is a nest of readable labelled
      // nodes, so screen readers should walk the text natively instead of it
      // being flattened to a single image label. The dispatcher leaves this
      // type unwrapped (describeDiagram returns null) for the same reason.
      aria-label={p.label ?? p.title ?? 'Decision tree'}
      className="my-3 overflow-x-auto rounded-md border border-border bg-card/60 p-3"
    >
      {p.title && (
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {p.title}
        </div>
      )}
      <Node node={p.root} isRoot />
    </figure>
  )
}
